'use client';

import { useEffect } from 'react';
import { User } from 'firebase/auth';
import { Session } from 'next-auth';
import { useRouter } from 'next/navigation';
import { isPublicPath, signInPathFor } from '@/lib/public-paths';

// Define an interface for the expected user structure within the NextAuth session
interface SessionUser {
  id?: string;
  role?: string;
  name?: string | null;
  email?: string | null;
  image?: string | null;
}

export interface UseAuthNavigationParams {
  loading: boolean;
  status: string;
  session: Session | null;
  user: User | null;
  userRole: string | null;
  currentViewRole: 'parent' | 'operator' | null;
  router: ReturnType<typeof useRouter>;
  studentDashGraceUntil: number | null;
  setStudentDashGraceUntil: (value: number | null) => void;
  postPaymentGraceUntil: number | null;
  setPostPaymentGraceUntil: (value: number | null) => void;
  isProfessional: boolean;
}

/**
 * Owns the redirect/navigation effect for the AuthProvider.
 * Public-route matching must stay in lockstep with middleware (`isPublicPath`).
 */
export function useAuthNavigation({
  loading,
  status,
  session,
  user,
  userRole,
  currentViewRole,
  router,
  studentDashGraceUntil,
  setStudentDashGraceUntil,
  postPaymentGraceUntil,
  setPostPaymentGraceUntil,
  isProfessional,
}: UseAuthNavigationParams): void {
    // ---------------------------
    // SECOND useEffect – Navigation / Redirection logic
    // ---------------------------

    // ---------------------------
    // REVISED Navigation / Redirection effect
    // This replaces the old, complex useEffect with a simpler, more declarative approach
    // ---------------------------
    useEffect(() => {
      // Wait until auth state & Next-Auth session have finished loading
      if (loading || status === 'loading') {
        console.log('AuthProvider Nav: Waiting for auth to complete...', { loading, status });
        return;
      }

      const hasWindow = typeof window !== 'undefined';
      const currentPath = hasWindow ? window.location.pathname : '';

      // School admins and teachers sign in with Firebase, not NextAuth.
      // /school/login hard-redirects here; NextAuth reports unauthenticated
      // immediately and Firebase IndexedDB restore is slower on phones. The
      // unauthenticated guard used to fire in that gap and dump them back on
      // the login page. Dual-role operator navigation would also yank them
      // off /teacher onto /operator/dashboard. These pages own their own auth
      // (useTeacherAuth / the school dashboard's onAuthStateChanged).
      //
      // Whole-segment match, same as middleware.ts and signInPathFor, so the
      // older /teacher-dashboard route is not swept in.
      const inSchoolArea = ['/school', '/teacher'].some(
        (p) => currentPath === p || currentPath.startsWith(`${p}/`),
      );
      if (inSchoolArea) {
        return;
      }

      const sessionUser = session?.user as SessionUser | undefined;
      const sessionRole = sessionUser?.role;
      const isNextAuthAuthenticated = status === 'authenticated' && !!sessionRole;
      const hasSessionWithRole = !!session && !!sessionRole;
      const hasFirebaseUser = !!user; // Treat Firebase-authenticated users (students) as authenticated

      // Check if returning from payment (used in multiple places below)
      let returningFromPayment = false;
      if (hasWindow) {
        try {
          returningFromPayment = sessionStorage.getItem('returnFromPayment') === 'true';
        } catch (err) {
          console.warn('AuthProvider Nav: Unable to read returnFromPayment flag:', err);
        }
      }

      console.log('AuthProvider Nav: DEBUG', {
        currentPath,
        loading,
        status,
        isNextAuthAuthenticated,
        hasSessionWithRole,
        sessionRole,
        currentViewRole,
        session: !!session,
        sessionUser: session?.user,
        firebaseUser: !!user,
        sessionUserRole: sessionUser?.role,
        rawSession: session
      });

      // --- 1. Handle Unauthenticated Users ---
      // Updated authentication check to be more robust
      const isReallyAuthenticated = isNextAuthAuthenticated || hasSessionWithRole || hasFirebaseUser;

      console.log('AuthProvider Nav: Authentication status:', {
        isNextAuthAuthenticated,
        hasSessionWithRole,
        isReallyAuthenticated,
        statusCheck: status === 'authenticated',
        sessionRoleCheck: !!sessionRole,
        hasSessionCheck: !!session,
        sessionUserRoleCheck: !!sessionRole
      });

      // SPECIAL CASE: If we're on any dashboard path and have any session, don't redirect
      // EXCEPT for dual-role users who need navigation between dashboards
      if ((currentPath.startsWith('/operator') || currentPath === '/dashboard' || currentPath.startsWith('/dashboard/')) && session && sessionRole) {
        // Don't treat a stale student session as valid access for operator routes.
        if (currentPath.startsWith('/operator') && sessionRole === 'student') {
          console.log('AuthProvider Nav: On operator path with student session role - continuing with navigation logic');
        } else if (sessionRole === 'both') {
          console.log('AuthProvider Nav: On dashboard path with dual-role session, allowing dual-role navigation logic');
          // Don't return here - let dual-role navigation logic run
        } else {
          console.log('AuthProvider Nav: On dashboard path with valid session, allowing access');
          return;
        }
      }

      if (!isReallyAuthenticated) {
        const onPaymentSuccessPage = currentPath.startsWith('/payment-success');
        const isPublicRoute = isPublicPath(currentPath);
        const paymentReturnGraceActive = onPaymentSuccessPage || returningFromPayment;
        if (paymentReturnGraceActive) {
          const now = Date.now();
          if (postPaymentGraceUntil === null) {
            const graceMs = 5000;
            setPostPaymentGraceUntil(now + graceMs);
            console.log('AuthProvider Nav: Starting payment-return grace window.', { graceMs, currentPath });
            return;
          }
          if (now < postPaymentGraceUntil) {
            console.log('AuthProvider Nav: Within payment-return grace window, skipping unauth redirect.', { until: postPaymentGraceUntil, now });
            return;
          }
          console.log('AuthProvider Nav: Payment-return grace window expired, proceeding with unauth redirect.');
          setPostPaymentGraceUntil(null);
          if (hasWindow) {
            sessionStorage.removeItem('returnFromPayment');
          }
        }
        // Stabilization: if navigating to a student dashboard and we have a Firebase user or a student_id hint, don't redirect yet
        const onStudentDashboard = currentPath.startsWith('/student-dashboard/');
        const hasStudentHint = hasWindow && !!localStorage.getItem('student_id');
        // New: Apply a short grace period even if we don't yet see Firebase user or hint
        if (onStudentDashboard) {
          const now = Date.now();
          if (studentDashGraceUntil === null) {
            const graceMs = 1800; // ~1.8s grace to allow onAuthStateChanged + claims
            setStudentDashGraceUntil(now + graceMs);
            console.log('AuthProvider Nav: Initiating student-dashboard grace window to avoid premature redirect.', { graceMs });
            return;
          }
          if (now < studentDashGraceUntil) {
            console.log('AuthProvider Nav: Within student-dashboard grace window, skipping unauth redirect.', { until: studentDashGraceUntil, now });
            return;
          }
        }
        if (onStudentDashboard && (hasFirebaseUser || hasStudentHint)) {
          console.log('AuthProvider Nav: Post-login stabilization on student dashboard. Skipping unauth redirect this cycle.', {
            onStudentDashboard,
            hasFirebaseUser,
            hasStudentHint,
          });
          return;
        }

        // If not on a public path, redirect to login -- the school sign-in for
        // the school area, which is where its own pages send people too.
        if (!isPublicRoute) {
          const signInPath = signInPathFor(currentPath);
          console.log(`AuthProvider Nav: Not authenticated, redirecting from ${currentPath} to ${signInPath}`, {
            isNextAuthAuthenticated,
            hasSessionWithRole,
            hasFirebaseUser,
          });
          router.replace(signInPath);
        }
        return; // Stop further processing when unauthenticated
      }

      console.log('AuthProvider Nav: User is authenticated, proceeding with navigation logic');
      if (postPaymentGraceUntil !== null) {
        setPostPaymentGraceUntil(null);
      }
      if (hasWindow && returningFromPayment) {
        sessionStorage.removeItem('returnFromPayment');
      }

      // --- 2. Handle Authenticated Users ---

      // If user is on the login page, get them out of there.
      if (currentPath.startsWith('/login') || currentPath.startsWith('/operator/login')) {
        // For dual-role users, go to the dashboard matching their selected view.
        // Use userRole from state, not sessionRole which may not be set yet
        const effectiveRole = sessionRole || userRole;

        // Students should not be redirected here - login page handles student redirect
        if (effectiveRole === 'student') {
          console.log('AuthProvider Nav: Student on login page, letting login page handle redirect');
          return;
        }

        // Check if user has isProfessional claim — redirect to professional dashboard
        const destination = isProfessional
          ? '/professional/dashboard'
          : effectiveRole === 'both'
            ? (currentViewRole === 'operator' ? '/operator/dashboard' : '/dashboard')
            : currentPath.startsWith('/operator/login')
              ? '/operator/dashboard'
              : '/dashboard'; // Default for parents

        console.log(`AuthProvider Nav: Authenticated on login page, redirecting to ${destination}`, {
          effectiveRole,
          currentViewRole,
          sessionRole,
          userRole
        });
        router.replace(destination);
        return;
      }

      // --- 3. Handle Dual-Role Navigation ---
      // This is the declarative navigation that replaces the setTimeout logic.
      const effectiveRole = sessionRole || userRole;
      if (effectiveRole === 'both') {
        // If the user's intent (currentViewRole) doesn't match their location, navigate them.
        if (currentViewRole === 'operator' && !currentPath.startsWith('/operator')) {
          console.log("AuthProvider Nav: Dual-role user wants 'operator' view, navigating to operator dashboard.", {
            currentViewRole,
            currentPath,
            effectiveRole
          });
          router.replace('/operator/dashboard');
          return; // Stop further processing after navigation
        }
        if (currentViewRole === 'parent' && currentPath.startsWith('/operator')) {
          console.log("AuthProvider Nav: Dual-role user wants 'parent' view, navigating to parent dashboard.", {
            currentViewRole,
            currentPath,
            effectiveRole
          });
          router.replace('/dashboard');
          return; // Stop further processing after navigation
        }

        // If a dual-role user is on either of their valid dashboards, DO NOTHING. Let them be.
        console.log("AuthProvider Nav: Dual-role user on a valid path, no redirect needed.", {
          currentViewRole,
          currentPath,
          effectiveRole
        });
        return;
      }

      // --- 3b. Handle Professional Users ---
      // Professional learners should always be on the professional dashboard, not the parent dashboard.
      if (isProfessional && !currentPath.startsWith('/professional')) {
        console.log('AuthProvider Nav: Professional user not on professional page, redirecting to /professional/dashboard.');
        router.replace('/professional/dashboard');
        return;
      }

      // --- 4. Handle Single-Role Users on Wrong Page ---
      // This logic now only applies to users who are NOT dual-role.
      if (sessionRole === 'parent' && currentPath.startsWith('/operator')) {
        console.log(`AuthProvider Nav: Parent on operator page, redirecting to /dashboard.`);
        router.replace('/dashboard');
      }

      // Note: A single-role 'operator' would be handled by the logic in step 2 if they land on '/'.
      // This covers all critical cases.

    }, [loading, status, session, user, currentViewRole, router, studentDashGraceUntil, postPaymentGraceUntil, isProfessional]); // Dependencies that drive navigation, include Firebase user and grace window
}
